<script lang="ts">
  import type { DealPipeline } from "../lib/api";
  import { haptic } from "../lib/tg";

  let {
    open = $bindable(false),
    pipelines = [] as DealPipeline[],
    currentPipelineId,
    currentStatusId,
    onpick,
  }: {
    open?: boolean;
    pipelines?: DealPipeline[];
    currentPipelineId: number;
    currentStatusId: number;
    onpick?: (pipelineId: number, statusId: number, stageName: string) => void;
  } = $props();

  const WON = 142;
  const LOST = 143;

  let tab = $state<number | null>(null);
  $effect(() => {
    if (open) tab = currentPipelineId;
  });

  const pipe = $derived(pipelines.find((p) => p.amo_id === tab) ?? pipelines[0]);
  const openStages = $derived((pipe?.statuses ?? []).filter((s) => s.amo_id !== WON && s.amo_id !== LOST));
  const closedStages = $derived((pipe?.statuses ?? []).filter((s) => s.amo_id === WON || s.amo_id === LOST));

  function isCurrent(statusId: number) {
    return pipe?.amo_id === currentPipelineId && statusId === currentStatusId;
  }

  function pick(statusId: number, name: string) {
    if (!pipe || isCurrent(statusId)) return;
    haptic("light");
    open = false;
    onpick?.(pipe.amo_id, statusId, name);
  }
</script>

{#if open}
  <div class="backdrop" onclick={() => (open = false)} role="presentation"></div>
  <div class="sheet" role="dialog" aria-label="Этап сделки">
    <div class="grab"></div>
    <div class="title">Перенести на этап</div>

    {#if pipelines.length > 1}
      <div class="tabs" role="tablist">
        {#each pipelines as p}
          <button class="tab" class:active={pipe?.amo_id === p.amo_id} role="tab"
                  aria-selected={pipe?.amo_id === p.amo_id}
                  onclick={() => (tab = p.amo_id)} type="button">{p.name}</button>
        {/each}
      </div>
    {/if}

    <ol class="stages">
      {#each openStages as s, i}
        <li>
          <button class="stage" class:current={isCurrent(s.amo_id)} onclick={() => pick(s.amo_id, s.name)} type="button">
            <span class="dot" style:background={s.color || "var(--border-strong)"}></span>
            <span class="num">{i + 1}</span>
            <span class="sname">{s.name}</span>
            {#if isCurrent(s.amo_id)}<span class="here">сейчас</span>{/if}
          </button>
        </li>
      {/each}
    </ol>

    {#if closedStages.length}
      <div class="closed">
        {#each closedStages as s}
          <button class="close-btn" class:won={s.amo_id === WON} class:lost={s.amo_id === LOST}
                  class:current={isCurrent(s.amo_id)}
                  onclick={() => pick(s.amo_id, s.name)} type="button">{s.name}</button>
        {/each}
      </div>
    {/if}
  </div>
{/if}

<style>
  .backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 60; animation: fade 160ms ease; }
  .sheet {
    position: fixed; left: 0; right: 0; bottom: 0;
    background: var(--bg-elev);
    border-top-left-radius: 22px; border-top-right-radius: 22px;
    padding: 12px 16px calc(18px + var(--safe-bottom, 0px));
    box-shadow: var(--shadow-lg);
    z-index: 70;
    animation: slide 220ms cubic-bezier(.2,.8,.2,1);
    max-height: 88vh; overflow-y: auto;
  }
  .grab { width: 36px; height: 4px; background: var(--border-strong); border-radius: 2px; margin: 0 auto 12px; }
  .title { font-family: 'Manrope', sans-serif; font-weight: 700; font-size: 15px; margin-bottom: 12px; }
  .tabs { display: flex; gap: 6px; margin-bottom: 10px; overflow-x: auto; }
  .tab {
    flex-shrink: 0; padding: 7px 12px; border-radius: 10px;
    background: var(--bg-elev-2); border: 1px solid var(--border);
    color: var(--text-mute); font-size: 13px; font-weight: 600; cursor: pointer;
  }
  .tab.active { color: var(--text); border-color: var(--ember); }
  .stages { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 4px; }
  .stage {
    width: 100%; display: flex; align-items: center; gap: 10px;
    padding: 12px; border-radius: 12px;
    background: transparent; border: 1px solid transparent;
    color: var(--text); font-size: 14px; text-align: left; cursor: pointer;
    transition: background 120ms, transform 100ms;
  }
  .stage:active { transform: scale(0.98); background: var(--bg-elev-2); }
  .stage.current { background: var(--bg-elev-2); border-color: var(--ember); cursor: default; }
  .dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
  .num { font-family: 'JetBrains Mono', monospace; font-size: 11px; color: var(--text-dim); width: 16px; }
  .sname { flex: 1; }
  .here { font-size: 11px; color: var(--ember); font-weight: 600; }
  .closed { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 12px; }
  .close-btn {
    min-height: 46px; padding: 8px; border-radius: 12px;
    background: var(--bg-elev-2); font-size: 13px; font-weight: 600; cursor: pointer;
    transition: transform 100ms;
  }
  .close-btn:active { transform: scale(0.97); }
  .close-btn.won { color: var(--success); border: 1px solid var(--success); }
  .close-btn.lost { color: var(--danger); border: 1px solid var(--danger); }
  .close-btn.current { opacity: 0.5; cursor: default; }
  @keyframes fade { from { opacity: 0; } to { opacity: 1; } }
  @keyframes slide { from { transform: translateY(100%); } to { transform: translateY(0); } }
  @media (prefers-reduced-motion: reduce) { .sheet, .backdrop { animation: none; } }
</style>
